import { PENTATONIC_OFFSETS, snapToScale, Timbre } from "./audio";
import { ConnectionGraph } from "./connections";
import { PITCH_INDEX_MAX, PITCH_INDEX_MIN } from "./constants";
import { ObjectField, pitchQuantizeMask } from "./objects";

/** Pitch classes (0..11) a 12-bit scale mask allows, ascending. */
export function maskToClasses(mask: number): number[] {
  const classes: number[] = [];
  for (let i = 0; i < 12; i++) {
    if ((mask >> i) & 1) {
      classes.push(i);
    }
  }
  return classes;
}

/**
 * The scale mask wired into the Timbre object that governs `timbreId`, or null
 * when nothing constrains it. A PitchQuantize node feeds its mask through
 * `pitchQuantizeOut → pitchQuantizeIn`; the resolver walks the first Timbre
 * object whose governed set includes `timbreId` and that has such an edge, and
 * reads the source node's mask (one PitchQuantize fans out to every timbre the
 * node governs; two nodes on one timbre resolve first-found, matching the
 * per-timbre transpose unsupported-duplicate rule).
 */
function connectedScaleMask(timbreId: Timbre, field: ObjectField, graph: ConnectionGraph): number | null {
  for (const obj of field.list()) {
    if (obj.type !== "timbre" || !obj.timbreIds.includes(timbreId)) {
      continue;
    }
    const edges = graph.edgesTo(obj.id, "pitchQuantizeIn");
    if (edges.length === 0) {
      continue;
    }
    const source = field.list().find((o): boolean => o.id === edges[0].from && o.type === "pitchQuantize");
    if (source) {
      return pitchQuantizeMask(source);
    }
  }
  return null;
}

/**
 * The pitch index `index` scale-degrees from the home degree, where home is the
 * allowed degree nearest pitchIndex 0. Value 0 returns home, 1 the next allowed
 * degree up, -1 the next down; the result is clamped to the
 * [PITCH_INDEX_MIN, PITCH_INDEX_MAX] degree list so each integer step lands on a
 * distinct scale note rather than collapsing onto a shared one. An empty
 * `classes` returns `index` unchanged.
 */
export function pitchByDegreeIndex(index: number, classes: readonly number[]): number {
  const degrees: number[] = [];
  for (let p = PITCH_INDEX_MIN; p <= PITCH_INDEX_MAX; p++) {
    if (classes.includes(((p % 12) + 12) % 12)) {
      degrees.push(p);
    }
  }
  if (degrees.length === 0) {
    return index;
  }
  const baseIdx = degrees.indexOf(snapToScale(0, classes));
  const target = baseIdx + Math.round(index);
  return degrees[Math.max(0, Math.min(target, degrees.length - 1))];
}

/**
 * The semitone classes a body of `timbreId` may step / spawn onto. Simple mode
 * is the top priority and forces the pentatonic set regardless of any
 * PitchQuantize wiring. Otherwise a connected mask constrains the set; an empty
 * mask (every class off) and the no-connection case both return null, meaning
 * "no restriction" so the caller steps the full chromatic range.
 */
export function allowedPitchClasses(
  timbreId: Timbre,
  simpleMode: boolean,
  field: ObjectField,
  graph: ConnectionGraph,
): readonly number[] | null {
  if (simpleMode) {
    return PENTATONIC_OFFSETS;
  }
  const mask = connectedScaleMask(timbreId, field, graph);
  if (mask === null) {
    return null;
  }
  const classes = maskToClasses(mask);
  return classes.length > 0 ? classes : null;
}
